import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BackButton } from '../../components/ui/BackButton';
import { assistantsApi } from '../../api/assistants';
import { useSportTypes } from '../../hooks/useSportTypes';

export function AssistantDetailScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const queryClient = useQueryClient();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const syncCurrentAssistant = useAuthStore((s) => s.syncCurrentAssistant);
  const selectedOrganizationId = useAuthStore((s) => s.selectedOrganizationId);

  const assistantId = currentScreen.params.assistantId as string;
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sport_name: '',
    description: '',
  });
  const [sportSuggestions, setSportSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: sportTypes } = useSportTypes();

  const { data: assistant, isLoading } = useQuery({
    queryKey: ['assistant', assistantId],
    queryFn: () => assistantsApi.getById(assistantId),
    enabled: !!assistantId,
  });

  useEffect(() => {
    if (assistant) {
      setFormData({
        name: assistant.name,
        sport_name: assistant.sport_name || '',
        description: assistant.description || '',
      });
    }
  }, [assistant]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      await assistantsApi.update(assistantId, {
        name: formData.name,
        sport_name: formData.sport_name || undefined,
        description: formData.description || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistant', assistantId] });
      queryClient.invalidateQueries({ queryKey: ['assistants'] });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      console.error('Failed to update assistant:', error);
      alert('Не удалось обновить направление. Попробуйте ещё раз.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => assistantsApi.delete(assistantId),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] });
      // If deleted the current direction, switch to another one
      if (assistantId === currentAssistantId && selectedOrganizationId) {
        try {
          const remaining = await assistantsApi.getByOrganizationId(selectedOrganizationId);
          const next = remaining.length > 0 ? remaining[0].id : null;
          await syncCurrentAssistant(next);
        } catch (err) {
          console.error('Failed to switch assistant after delete:', err);
          await syncCurrentAssistant(null);
        }
      }
      popScreen();
    },
    onError: (error: Error) => {
      console.error('Failed to delete assistant:', error);
      alert('Не удалось удалить направление. Попробуйте ещё раз.');
    },
  });

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Название направления обязательно');
      return;
    }
    if (!formData.sport_name.trim()) {
      alert('Вид спорта обязателен');
      return;
    }
    updateMutation.mutate();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleSportNameChange = (value: string) => {
    setFormData({ ...formData, sport_name: value });
    if (value.length > 0 && sportTypes) {
      const filtered = sportTypes
        .filter((st) => st.name.toLowerCase().includes(value.toLowerCase()))
        .map((st) => st.name);
      setSportSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSportSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSport = (sport: string) => {
    setFormData({ ...formData, sport_name: sport });
    setShowSuggestions(false);
  };

  const handleBack = () => {
    if (isEditing) {
      setIsEditing(false);
    } else {
      popScreen();
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-hint)',
    marginBottom: '4px',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  if (!assistant) {
    return (
      <div className="p-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>
            Направление не найдено
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <BackButton onClick={handleBack} />
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-[15px] font-medium"
            style={{ color: 'var(--accent)' }}
          >
            Редактировать
          </button>
        )}
      </div>

      <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
        {isEditing ? 'Редактирование' : assistant.name}
      </h1>

      <Card>
        <div className="space-y-4">
          {isEditing ? (
            <>
              {/* Name */}
              <div>
                <label style={labelStyle}>Название *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Название направления"
                  style={inputStyle}
                  disabled={updateMutation.isPending}
                />
              </div>

              {/* Sport type with autocomplete */}
              <div className="relative">
                <label style={labelStyle}>Вид спорта *</label>
                <input
                  type="text"
                  value={formData.sport_name}
                  onChange={(e) => handleSportNameChange(e.target.value)}
                  onFocus={() => {
                    if (formData.sport_name && sportSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Начните вводить вид спорта"
                  style={inputStyle}
                  disabled={updateMutation.isPending}
                />
                {showSuggestions && sportSuggestions.length > 0 && (
                  <div
                    className="absolute z-10 w-full mt-1 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      border: '1px solid var(--separator)',
                    }}
                  >
                    {sportSuggestions.map((sport) => (
                      <button
                        key={sport}
                        type="button"
                        onClick={() => handleSelectSport(sport)}
                        className="w-full px-3 py-2 text-left text-[15px] active:opacity-60"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {sport}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Краткое описание (необязательно)"
                  rows={3}
                  style={{ ...inputStyle, resize: 'none' }}
                  disabled={updateMutation.isPending}
                />
              </div>

              {/* Save/Cancel */}
              <div className="flex gap-2 pt-4" style={{ borderTop: '1px solid var(--separator)' }}>
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  fullWidth
                  loading={updateMutation.isPending}
                >
                  Сохранить
                </Button>
                <Button onClick={() => setIsEditing(false)} variant="secondary">
                  Отмена
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
                  Вид спорта
                </p>
                <p className="text-[17px]" style={{ color: 'var(--text-primary)' }}>
                  {assistant.sport_name}
                </p>
              </div>

              {assistant.description && (
                <div>
                  <p className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
                    Описание
                  </p>
                  <p
                    className="text-[15px] whitespace-pre-wrap"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {assistant.description}
                  </p>
                </div>
              )}

              <div
                className="space-y-2 pt-4"
                style={{ borderTop: '1px solid var(--separator)' }}
              >
                <Button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  variant="danger"
                  fullWidth
                  loading={deleteMutation.isPending}
                >
                  Удалить направление
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center" onClick={() => setShowDeleteConfirm(false)}>
          <div className="mx-4 rounded-xl p-6 max-w-sm w-full" style={{ backgroundColor: 'var(--bg-primary)' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[17px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Удалить направление?</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-hint)' }}>
              Направление "{assistant.name}" и все связанные данные будут удалены.
            </p>
            <div className="flex gap-2">
              <Button variant="danger" onClick={() => { setShowDeleteConfirm(false); deleteMutation.mutate(); }} fullWidth>Удалить</Button>
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} fullWidth>Отмена</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
